export class CarouselComponent {
    constructor() {
        Array.from(document.querySelectorAll('[data-carousel]')).forEach((element: HTMLElement) => {
            new Carousel(element);
        });
    }
}

const DRAG_START_THRESHOLD = 8;

class Carousel {
    private root: HTMLElement;
    private viewport: HTMLElement;
    private track: HTMLElement;
    private slides: Array<HTMLElement>;
    private prevButton: HTMLButtonElement;
    private nextButton: HTMLButtonElement;
    private dotsContainer: HTMLElement;
    private dots: Array<HTMLButtonElement> = [];
    private index = 0;

    private pointerId: number = null;
    private startX = 0;
    private startY = 0;
    private delta = 0;
    private dragging = false;
    private dragged = false;

    constructor(root: HTMLElement) {
        this.root = root;
        this.viewport = root.querySelector('[data-carousel-viewport]');
        this.track = root.querySelector('[data-carousel-track]');
        this.slides = Array.from(root.querySelectorAll('.carousel__slide'));
        this.prevButton = root.querySelector('[data-carousel-prev]');
        this.nextButton = root.querySelector('[data-carousel-next]');
        this.dotsContainer = root.querySelector('[data-carousel-dots]');

        if (!this.viewport || !this.track || this.slides.length < 2) return;

        this.initDots();
        this.initButtons();
        this.initKeyboardEvent();
        this.initDragEvents();

        this.root.classList.add('carousel--ready');
        this.render();
    }

    private initDots() {
        if (!this.dotsContainer) return;

        this.slides.forEach((slide, i) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'carousel__dot';
            dot.setAttribute('role', 'tab');
            dot.setAttribute('aria-label', `Ga naar foto ${i + 1}`);
            dot.addEventListener('click', () => this.goTo(i));
            this.dotsContainer.appendChild(dot);
            this.dots.push(dot);
        });
    }

    private initButtons() {
        if (this.prevButton) {
            this.prevButton.addEventListener('click', () => this.goTo(this.index - 1));
        }
        if (this.nextButton) {
            this.nextButton.addEventListener('click', () => this.goTo(this.index + 1));
        }
    }

    private initKeyboardEvent() {
        this.viewport.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                this.goTo(this.index - 1);
            }
            if (e.key === 'ArrowRight') {
                e.preventDefault();
                this.goTo(this.index + 1);
            }
        });
    }

    /// Swiping. The viewport has `touch-action: pan-y`, so vertical gestures keep
    /// scrolling the page and only horizontal ones reach us.
    private initDragEvents() {
        this.viewport.addEventListener('pointerdown', (e: PointerEvent) => {
            if (this.pointerId !== null || e.button !== 0) return;

            this.pointerId = e.pointerId;
            this.startX = e.clientX;
            this.startY = e.clientY;
            this.delta = 0;
            this.dragging = false;
            this.dragged = false;
        });

        this.viewport.addEventListener('pointermove', (e: PointerEvent) => {
            if (e.pointerId !== this.pointerId) return;

            const dx = e.clientX - this.startX;
            const dy = e.clientY - this.startY;

            if (!this.dragging) {
                if (Math.abs(dx) < DRAG_START_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;

                this.dragging = true;
                this.dragged = true;
                this.track.classList.add('carousel__track--dragging');

                try {
                    this.viewport.setPointerCapture(e.pointerId);
                } catch (error) {
                    // The pointer went away between the move and here; the drag
                    // still works, it just stops at the edge of the viewport.
                }
            }

            this.delta = dx;
            this.track.style.transform = `translate3d(calc(${this.index * -100}% + ${dx}px), 0, 0)`;
        });

        const endDrag = (e: PointerEvent) => {
            if (e.pointerId !== this.pointerId) return;

            const wasDragging = this.dragging;
            const delta = this.delta;

            this.pointerId = null;
            this.dragging = false;
            this.delta = 0;
            this.track.classList.remove('carousel__track--dragging');

            if (!wasDragging) return;

            const threshold = Math.min(80, this.viewport.clientWidth * 0.15);
            if (delta <= -threshold) {
                this.goTo(this.index + 1);
            } else if (delta >= threshold) {
                this.goTo(this.index - 1);
            } else {
                this.render();
            }
        };

        this.viewport.addEventListener('pointerup', endDrag);
        this.viewport.addEventListener('pointercancel', endDrag);

        /// A swipe ends on a slide, so swallow the click it would trigger there.
        this.viewport.addEventListener(
            'click',
            (e: MouseEvent) => {
                if (!this.dragged) return;

                this.dragged = false;
                e.preventDefault();
                e.stopPropagation();
            },
            true
        );
    }

    private goTo(index: number) {
        this.index = Math.min(Math.max(index, 0), this.slides.length - 1);
        this.render();
    }

    private render() {
        this.track.style.transform = `translate3d(${this.index * -100}%, 0, 0)`;

        if (this.prevButton) this.prevButton.disabled = this.index === 0;
        if (this.nextButton) this.nextButton.disabled = this.index === this.slides.length - 1;

        this.dots.forEach((dot, i) => {
            dot.setAttribute('aria-selected', i === this.index ? 'true' : 'false');
        });

        this.preloadNeighbours();
    }

    /// The viewport clips its overflow, so the browser never considers the other
    /// slides visible and native lazy loading never fires for them. Wake up the
    /// current slide and its direct neighbours by hand.
    private preloadNeighbours() {
        for (let i = this.index - 1; i <= this.index + 1; i++) {
            const slide = this.slides[i];
            if (!slide) continue;

            const image = slide.querySelector('img');
            if (image && image.getAttribute('loading') === 'lazy') {
                image.setAttribute('loading', 'eager');
            }
        }
    }
}
